// src/components/details/DetailsBanner.tsx
'use client';
import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { toAbsoluteMediaUrl } from '@/lib/media';
import { cn } from '@/lib/utils';
import { BadgeCheck, Lock, MapPin } from 'lucide-react';
import ProducerBadge from '@/components/common/ProducerBadge';
import type { ProducerRole } from '@/lib/producerLabel';

function formatJoinDate(input: string | Date | null | undefined): string | null {
  if (!input) return null;
  try {
    const d = input instanceof Date ? input : new Date(input);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  } catch {
    return null;
  }
}

export default function DetailsBanner({
  bannerUrl,
  displayName,
  avatarUrl,
  profileUrl: _profileUrl,
  personId: _personId,
  minimal = false,
  unsafe: _unsafe = false,
  isVerified = false,
  isProducer = false,
  producerRole = null,
  headline,
  availabilityStatus,
  age,
  gender,
  location,
  metroSlug,
  memberSince,
  presenceOnline,
  actions,
  joinHref,
}: {
  bannerUrl?: string | null;
  displayName?: string;
  avatarUrl?: string | null;
  profileUrl?: string | null;
  personId?: string | number | null;
  minimal?: boolean;
  unsafe?: boolean;
  isVerified?: boolean;
  /**
   * The public Producer label (a self-declaration, not an ID check). The shared ProducerBadge on
   * its own line under the name, never inside or styled like the Verified pill.
   */
  isProducer?: boolean;
  /** "producer_and_talent" reads "Producer and talent"; anything else "Producer". */
  producerRole?: ProducerRole | null;
  headline?: string | null;
  availabilityStatus?: string | null;
  age?: number | null;
  gender?: string | null;
  location?: string | null;
  metroSlug?: string | null;
  memberSince?: string | Date | null;
  presenceOnline?: boolean | null;
  actions?: React.ReactNode;
  /** Guest-only: when set, renders a "Members see photos" pill linking to signup. */
  joinHref?: string | null;
}) {
  // Media served for guests can 403 (missing _blurred derivatives); a fixed
  // src check can't catch that, only a load error can. Fall back to the
  // brand gradient / default avatar instead of rendering broken-image glyphs.
  const [bannerFailed, setBannerFailed] = React.useState(false);
  const [avatarFailed, setAvatarFailed] = React.useState(false);
  // DB display names occasionally carry doubled/trailing whitespace, which
  // leaks into the h1 and image alt text. Normalize once here.
  const name = (displayName || '').replace(/\s+/g, ' ').trim();
  // Banner: API sends correct blurred URLs for guests - just use as-is
  const src = (() => {
    if (!bannerUrl) return null;
    const abs = toAbsoluteMediaUrl(bannerUrl);
    try {
      const u = new URL(abs);
      u.pathname = u.pathname.replace(/_banner_thumb(\.[a-z0-9]+)$/i, '_banner$1');
      return u.toString();
    } catch {
      return abs.replace(/_banner_thumb(\.[a-z0-9]+)$/i, '_banner$1');
    }
  })();

  // Avatar: API already returns the correct URL for this viewer (blurred for guests,
  // original for authed). Just resolve it — no client-side gating needed.
  const avatar = (() => {
    if (!avatarUrl) return '/img/default-avatar.png';
    const raw = String(avatarUrl).trim().replace(/^\/+/, '');
    if (raw.includes('_blurred')) return toAbsoluteMediaUrl(raw);
    const base = raw.replace(/_(thumb|avatar)(\.[a-z0-9]+)$/i, '$2');
    const thumbKey = base.replace(/(\.[a-z0-9]+)$/i, '_thumb$1');
    return toAbsoluteMediaUrl(thumbKey);
  })();

  const since = formatJoinDate(memberSince);
  // Sub-line parts as nodes (not a joined string) so location can be a link to
  // its metro jobs hub when metroSlug is present. The /details caller doesn't
  // pass metroSlug, so location renders as plain text there exactly as before.
  const loc = location && String(location).trim() ? String(location).trim() : null;
  const slug = metroSlug && String(metroSlug).trim() ? String(metroSlug).trim() : null;
  const subBits: React.ReactNode[] = [];
  // Age lives in the meta line (matching the talent-card "age · sex · city"
  // vocabulary), not inside the h1 — keeps the document title clean.
  if (typeof age === 'number' && age > 0) subBits.push(String(age));
  if (loc) {
    subBits.push(
      slug
        ? <Link key="loc" href={`/posts/${slug}`} className="hover:underline">{loc}</Link>
        : loc
    );
  }
  if (gender && String(gender).trim()) subBits.push(String(gender).trim());
  if (since) subBits.push(`Member since ${since}`);

  return (
    <section className="w-full relative" aria-label="Profile banner">
      {/* Fixed height only when minimal (no overlay content). With content, the
          box must grow: on narrow screens the identity + actions stack is taller
          than 260px, and a fixed height clips the display name off the top. */}
      <div
        className={cn(
          'relative w-full overflow-hidden',
          minimal
            ? 'h-[260px] md:h-[300px]'
            : 'flex min-h-[260px] flex-col justify-end md:min-h-[300px]'
        )}
      >
        {src && !bannerFailed ? (
          <Image
            src={src}
            alt={name ? `${name} banner` : 'Profile banner'}
            fill
            priority
            sizes="100vw"
            className="absolute inset-0 object-cover"
            style={{ objectPosition: 'center' }}
            onError={() => setBannerFailed(true)}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-b from-[#F5E9D8] via-[#E8B8C4] to-[hsl(var(--primary))] dark:bg-gradient-to-br dark:from-fuchsia-950 dark:via-purple-900 dark:to-rose-950" aria-hidden>
            <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_25%_25%,rgba(236,72,153,0.55)_0%,transparent_55%),radial-gradient(circle_at_85%_70%,rgba(244,63,94,0.5)_0%,transparent_55%),radial-gradient(circle_at_50%_50%,rgba(139,92,246,0.35)_0%,transparent_60%)]" />
          </div>
        )}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/30 via-black/30 to-black/75" aria-hidden />

        {!minimal && (
          <div className="relative pt-16 md:pt-20">
            <div className="container mx-auto px-4 md:px-6 pb-5 md:pb-6">
              <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                {/* Left: avatar + identity */}
                <div className="flex items-end gap-4 md:gap-5 min-w-0">
                  <div className="relative h-20 w-20 md:h-28 md:w-28 shrink-0">
                    <Image
                      src={avatarFailed ? '/img/default-avatar.png' : avatar}
                      alt={name ? `${name} avatar` : 'Profile avatar'}
                      fill
                      sizes="(max-width: 768px) 80px, 112px"
                      className="rounded-full ring-4 ring-black/40 dark:ring-black/70 object-cover shadow-lg"
                      priority
                      onError={() => setAvatarFailed(true)}
                    />
                    {presenceOnline && (
                      <span
                        aria-hidden
                        className="absolute bottom-1 right-1 h-3.5 w-3.5 md:h-4 md:w-4 rounded-full bg-emerald-400 ring-2 ring-black/70"
                      />
                    )}
                  </div>
                  <div id="banner-avatar-sentinel" className="h-1 w-1 opacity-0 pointer-events-none" aria-hidden />
                  <div className="min-w-0 pb-1">
                    {name && (
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                        <h1 className="font-serif text-3xl md:text-5xl font-semibold leading-[1.05] tracking-tight text-white drop-shadow">
                          {name}
                        </h1>
                        {isVerified && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/20 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-300 ring-1 ring-emerald-400/40">
                            <BadgeCheck className="h-3 w-3" aria-hidden />
                            Verified
                          </span>
                        )}
                        {presenceOnline && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-300 ring-1 ring-emerald-400/30">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                            Online now
                          </span>
                        )}
                      </div>
                    )}
                    {name && isProducer && (
                      <div className="mt-2 flex" data-testid="producer-label">
                        <ProducerBadge role={producerRole} tone="dark" className="text-xs" />
                      </div>
                    )}
                    {subBits.length > 0 && (
                      // flex-wrap, not inline-flex nowrap: with nowrap the last bit
                      // ("Member since …") gets squeezed past the right screen edge
                      // on phones instead of wrapping to the next line.
                      <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm text-white/75">
                        <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        {subBits.map((bit, i) => (
                          <span key={i} className="inline-flex items-center whitespace-nowrap">
                            {i > 0 && <span aria-hidden className="mr-1.5">·</span>}
                            {bit}
                          </span>
                        ))}
                      </p>
                    )}
                    {headline && (
                      <p className="mt-1 max-w-lg truncate text-sm text-white/70">{headline}</p>
                    )}
                    {availabilityStatus && (
                      <span
                        className={cn(
                          'mt-2 inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold',
                          availabilityStatus === 'available' && 'bg-emerald-500/90 text-white',
                          availabilityStatus === 'hiring' && 'bg-blue-500/90 text-white',
                          availabilityStatus === 'collab_only' && 'bg-violet-500/90 text-white',
                          availabilityStatus === 'on_hiatus' && 'bg-zinc-600/80 text-white/80',
                        )}
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" />
                        {availabilityStatus === 'available' && 'Available'}
                        {availabilityStatus === 'hiring' && 'Hiring'}
                        {availabilityStatus === 'collab_only' && 'Collab only'}
                        {availabilityStatus === 'on_hiatus' && 'On hiatus'}
                      </span>
                    )}
                    {joinHref && (
                      // Guests see blurred media; without this, gating reads as a
                      // broken site instead of a membership perk.
                      <Link
                        href={joinHref}
                        className="mt-2 ml-0 inline-flex w-fit items-center gap-1.5 rounded-full bg-black/55 px-3 py-1.5 text-xs font-semibold text-white ring-1 ring-white/25 backdrop-blur-sm hover:bg-black/70 sm:ml-2"
                      >
                        <Lock className="h-3.5 w-3.5" aria-hidden />
                        Members see photos. Join free
                      </Link>
                    )}
                  </div>
                </div>

                {/* Right: actions slot */}
                {actions && (
                  <div className="flex items-center md:items-end md:justify-end shrink-0">
                    {actions}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        <span className="sr-only">{name ? `${name} banner` : 'Profile banner'}</span>
      </div>
    </section>
  );
}
